import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Lock, RotateCcw } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSite } from "@/context/SiteContext";
import { api, apiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useQuote } from "@/lib/useQuote";
import { loadRazorpay } from "@/lib/razorpay";
import { inr } from "@/lib/format";
import { btn } from "@/lib/ui";
import { CheckoutForm, validateCheckout } from "@/components/shop/CheckoutForm";
import { OrderLines, Totals } from "@/components/shop/OrderLines";
import { ProductDisclaimer } from "@/components/shop/ProductDetails";
import { CallButton, WhatsAppButton } from "@/components/common/ContactButtons";

const EMPTY = { name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", pincode: "" };
const DISCLAIMER = "This is an Ayurvedic proprietary medicine. It is not intended to diagnose, treat, cure or prevent any disease and does not replace medical consultation or treatment. Read the label carefully before use.";

const PaymentAlert = ({ status, onRetry }) => {
  if (status.state === "unavailable") {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" role="alert" data-testid="payment-unavailable">
        <p className="text-base font-semibold text-amber-950">{status.message}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <CallButton size="sm" label="Call to order" testid="checkout-call-btn" />
          <WhatsAppButton size="sm" message="Hello, I would like to order Spine-Fit Tablets." testid="checkout-whatsapp-btn" />
        </div>
      </div>
    );
  }
  if (status.state !== "failed") return null;
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5" role="alert" data-testid="payment-failed">
      <p className="flex items-start gap-2 text-base font-semibold text-red-900"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />{status.message}</p>
      <p className="mt-1 text-sm text-red-900">Your cart is unchanged. You can try the payment again.</p>
      <button type="button" onClick={onRetry} className={`${btn("green", "md")} mt-4`} data-testid="retry-payment-btn">
        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Retry Payment
      </button>
    </div>
  );
};

function payLabel(state, quote) {
  if (state === "processing") return "Opening secure payment…";
  if (state === "verifying") return "Confirming payment…";
  return `Pay ${quote ? inr(quote.total) : ""}`;
}

const OrderingOff = () => (
  <div className="container-x py-20 text-center md:py-28" data-testid="checkout-ordering-off">
    <h1 className="font-heading text-3xl font-extrabold tracking-tight text-brand-ink sm:text-4xl">Order on WhatsApp</h1>
    <p className="mx-auto mt-3 max-w-lg text-base text-slate-600">Online ordering is not available right now. Message us on WhatsApp or call us to place your order.</p>
    <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
      <WhatsAppButton size="lg" label="Order on WhatsApp" message="Hello, I would like to order Spine-Fit Tablets." testid="checkout-off-whatsapp-btn" />
      <CallButton size="lg" label="Call to order" testid="checkout-off-call-btn" />
    </div>
    <Link to="/shop" className="text-link mt-8" data-testid="checkout-off-shop-link">Back to the shop</Link>
  </div>
);

export default function Checkout() {
  const { settings } = useSite();
  useSeo({ title: "Checkout | Young Spine Tricity", description: "Secure checkout for Spine-Fit tablets.", path: "/checkout", noindex: true });
  return settings.online_payments_enabled ? <CheckoutFlow /> : <OrderingOff />;
}

function CheckoutFlow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const cart = useCart();
  const { settings } = useSite();
  const buyId = params.get("buy");
  const buyQty = Math.max(1, parseInt(params.get("qty") || "1", 10) || 1);
  const items = useMemo(
    () => (buyId ? [{ product_id: buyId, qty: buyQty }] : cart.items.map((i) => ({ product_id: i.product_id, qty: i.qty }))),
    [buyId, buyQty, cart.items]
  );
  const { quote, loading } = useQuote(items);
  const [form, setForm] = useState(EMPTY);
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pay = async () => {
    const errs = validateCheckout(form, agree);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(`co-${Object.keys(errs)[0] === "agree" ? "name" : Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setStatus({ state: "processing", message: "" });
    try {
      const { data } = await api.post("/checkout/create", { customer: form, items, agree_terms: agree, source: buyId ? "buy_now" : "cart" });
      if (!(await loadRazorpay())) throw new Error("razorpay");
      const markFailed = (reason) => api.post("/checkout/failed", { order_id: data.order_id, reason }).catch(() => {});
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.razorpay_order_id,
        name: "Young Spine Tricity", description: `Order ${data.order_number}`, image: `${window.location.origin}/logo192.png`,
        prefill: data.prefill, theme: { color: "#014E96" },
        handler: async (resp) => {
          setStatus({ state: "verifying", message: "" });
          try {
            await api.post("/checkout/verify", { order_id: data.order_id, ...resp });
            if (!buyId) cart.clear();
            navigate(`/order/${data.order_id}?t=${data.access_token}`, { replace: true });
          } catch {
            navigate(`/order/${data.order_id}?t=${data.access_token}`, { replace: true });
          }
        },
        modal: {
          ondismiss: () => {
            markFailed("Payment window closed");
            setStatus((s) => (["verifying", "failed"].includes(s.state) ? s : { state: "failed", message: "Payment was cancelled." }));
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        markFailed(resp?.error?.description || "Payment failed");
        setStatus({ state: "failed", message: `Payment failed: ${resp?.error?.description || "the payment could not be completed."}` });
      });
      rzp.open();
    } catch (e) {
      if (e?.response?.status === 503) setStatus({ state: "unavailable", message: apiError(e) });
      else setStatus({ state: "failed", message: e?.message === "razorpay" ? "Could not load the payment window. Please check your connection." : apiError(e) });
    }
  };

  if (!items.length) {
    return (
      <div className="container-x py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-heading text-3xl font-bold text-brand-ink">Your cart is empty</h1>
        <Link to="/shop" className={`${btn("green", "md")} mt-6`}>Visit the shop</Link>
      </div>
    );
  }
  const blocked = loading || !quote || quote.errors.length > 0 || ["processing", "verifying"].includes(status.state);

  return (
    <div className="bg-brand-tint pb-20">
      <div className="container-x py-10 md:py-14">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-brand-ink sm:text-4xl">Checkout</h1>
        <p className="mt-2 flex items-center gap-2 text-sm text-slate-600"><Lock className="h-4 w-4" aria-hidden="true" /> Guest checkout · Secure payment by Razorpay · Delivery within India</p>
        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          <section className="rounded-3xl bg-white p-5 ring-1 ring-slate-200 sm:p-8 lg:col-span-7" aria-labelledby="delivery-heading">
            <h2 id="delivery-heading" className="mb-6 text-xl font-bold text-brand-ink">Delivery details</h2>
            <CheckoutForm form={form} setField={setField} errors={errors} agree={agree} setAgree={setAgree} />
          </section>
          <aside className="space-y-5 lg:col-span-5" aria-labelledby="summary-heading">
            <div className="rounded-3xl bg-white p-5 ring-1 ring-slate-200 sm:p-8 lg:sticky lg:top-28">
              <h2 id="summary-heading" className="text-xl font-bold text-brand-ink">Order summary</h2>
              {quote ? <OrderLines lines={quote.lines} /> : <p className="py-4 text-sm text-slate-600">Loading…</p>}
              {quote?.errors.map((e) => <p key={e.product_id} className="field-error mb-2" role="alert" data-testid="checkout-stock-error">{e.message}</p>)}
              {quote && <Totals {...quote} totalLabel="Total to pay" />}
              <p className="mt-3 text-sm text-slate-600">{settings.delivery_text}</p>
              <div className="mt-5 space-y-4">
                <PaymentAlert status={status} onRetry={pay} />
                {!["failed", "unavailable"].includes(status.state) && (
                  <button type="button" onClick={pay} disabled={blocked} className={`${btn("green", "lg")} w-full`} data-testid="checkout-pay-btn">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                    {payLabel(status.state, quote)}
                  </button>
                )}
                <p className="text-center text-xs text-slate-600">UPI · Credit &amp; debit cards · Net banking · Wallets. No cash on delivery.</p>
              </div>
              <ProductDisclaimer text={DISCLAIMER} className="mt-5" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
